import Head from 'next/head';
import { useRouter } from 'next/router';
import ErrorPage from 'next/error';
import { gql } from '@apollo/client';
import { useQuery } from '@helpers/async';
import { initializeApollo, addApolloState } from '../../apollo-client';
import { Container, Box, Text, Flex, Grid, GridItem, Heading, AspectRatio } from '@chakra-ui/react';
import NextNprogress from 'nextjs-progressbar';

import GutenbergStylesheet from '../../styles/gutenberg';
import Layout from '../../components/resources/Layout';
import RelatedPosts from '../../components/resources/RelatedPosts';
import BlueCTA from '../../components/resources/BlueCTA';
import ImageShimmer from '../../components/ImageShimmer/ImageShimmer';

import sharecardImage from '../../public/images/resources/sharecard.jpg';
import sitePath from '../../helpers/sitePath';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';
import { useGetUtmCookies } from '../../hooks/utmCookies';

import {
  primaryCategory,
  filterPostsByCategory,
  filterSinglePostByCategory,
} from '../../helpers/categoryFilters';
import { addParamsToRelatedPost } from '../../helpers/addParamsToRelatedPost';
import metaRobots from '../../helpers/metaRobots';
import {
  EmailShareButton,
  FacebookShareButton,
  LinkedinShareButton,
  TwitterShareButton,
} from 'react-share';
import { ArrowLeftIcon, Button, Link } from '@terminal/design-system';

const flags = {
  shouldShowVideoWithoutFormSubmittion: true,
};

const GET_WEBINAR = gql`
  query GetWebinar($slug: ID!) {
    webinar(id: $slug, idType: SLUG) {
      id
      date
      title
      seo {
        title
        metaDesc
        metaRobotsNofollow
        metaRobotsNoindex
      }
      webinarsACF {
        date
        time
        pardotFormUrl
        heroImage {
          sourceUrl
          altText
        }
        description
        text
        youtube
        speakers {
          name
          jobTitle
          image {
            sourceUrl
            altText
          }
        }
      }
      featuredImage {
        node {
          altText
          sourceUrl
        }
      }
      categories {
        edges {
          isPrimary
          node {
            name
          }
        }
      }
      relatedWebinars {
        posts {
          ... on Webinar {
            id
            slug
            title
            categories {
              edges {
                isPrimary
                node {
                  name
                }
              }
            }
            featuredImage {
              node {
                altText
                sourceUrl
              }
            }
          }
          ... on Guide {
            id
            slug
            title
            categories {
              edges {
                isPrimary
                node {
                  name
                }
              }
            }
            featuredImage {
              node {
                altText
                sourceUrl
              }
            }
            guidePostType {
              guideType
              externalLink {
                url
              }
            }
          }
        }
      }
    }
    webinars(first: 100) {
      nodes {
        id
        date
        slug
        title
        categories {
          edges {
            isPrimary
            node {
              name
            }
          }
        }
        featuredImage {
          node {
            altText
            sourceUrl
          }
        }
      }
    }
  }
`;

export async function getServerSideProps({ query, locale }) {
  const { slug } = query;

  const apolloClient = initializeApollo();

  const { data } = await apolloClient.query({
    query: GET_WEBINAR,
    variables: {
      slug,
    },
  });

  if (!data.webinar) {
    return {
      notFound: true,
    };
  }

  return addApolloState(apolloClient, {
    props: {
      ...(await serverSideTranslations(locale)),
    },
  });
}

export default function Webinar() {
  const router = useRouter();

  const utmParams = useGetUtmCookies();

  const { slug } = router.query;

  const { loading, error, data } = useQuery(GET_WEBINAR, {
    variables: {
      slug,
    },
  });

  // CSR loading
  if (loading)
    return (
      <NextNprogress
        color="#2D445D"
        startPosition={0.3}
        stopDelayMs={200}
        height={3}
        showOnShallow={true}
      />
    );
  // Apollo error
  if (error) return <ErrorPage statusCode="500" />;

  const post = data.webinar;
  const relatedPosts = data.webinar ? data.webinar.relatedWebinars.posts : null;

  // get all post types
  const allResources = [].concat(data.webinars.nodes);

  // sort by date
  const allSortedResources = allResources.sort((a, b) => (a.date < b.date ? 1 : -1));

  const latestPosts = filterPostsByCategory(
    allSortedResources,
    primaryCategory(data.webinar),
  ).slice(0, 4);

  // add path for post type
  let relatedPostsWithParams = null;

  if (relatedPosts) {
    relatedPostsWithParams = addParamsToRelatedPost(relatedPosts);
  } else {
    relatedPostsWithParams = addParamsToRelatedPost(latestPosts);
  }

  const postImage = post.featuredImage ? post.featuredImage.node.sourceUrl : sharecardImage.src;

  const date = new Date(post.webinarsACF.date).toLocaleString('en-US', {
    // year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
  return (
    <>
      <Head>
        <link rel="canonical" href={sitePath(router.asPath)} />
        <title>{post.seo.title}</title>
        <meta name="description" content={post.seo.metaDesc} />
        <meta property="og:title" content={post.seo.title} />
        <meta property="og:description" content={post.seo.metaDesc} />
        <meta property="og:url" content={sitePath(router.asPath)} />
        <meta property="og:image" content={postImage} />
        <meta
          name="robots"
          content={metaRobots({
            translated: false,
            locale: router.locale,
            post,
          })}
        />
      </Head>
      <GutenbergStylesheet />
      <Layout type={filterSinglePostByCategory(post, 'Demand') ? 'businesses' : 'engineers'}>
        <Container fontFamily="body">
          <Box py={3}>
            <Button
              as={Link}
              variant="ghost"
              size="sm"
              leftIcon={<ArrowLeftIcon />}
              href="/resources"
            >
              Go back to Resources
            </Button>
          </Box>
          {!flags.shouldShowVideoWithoutFormSubmittion &&
          (filterSinglePostByCategory(post, 'Upcoming') || !router.query.watchnow) ? (
            <>
              <Grid
                templateColumns={{ base: '1fr', lg: '26.875rem 1fr' }}
                gap={{ base: '3rem', lg: '2.5rem 6rem' }}
                mb={{ base: '14', lg: '8rem' }}
              >
                {filterSinglePostByCategory(post, 'Webinar Series') && (
                  <>
                    <GridItem>
                      {post.webinarsACF.heroImage && post.webinarsACF.heroImage.sourceUrl && (
                        <ImageShimmer
                          src={post.webinarsACF.heroImage.sourceUrl}
                          alt={post.webinarsACF.heroImage.altText}
                          width="480"
                          height="280"
                          style={{ objectFit: 'contain' }}
                          layout="intrinsic"
                        />
                      )}
                    </GridItem>
                    <GridItem>
                      <Box
                        fontFamily="body"
                        fontSize="0.625rem"
                        lineHeight="0.8125rem"
                        fontWeight="600"
                        color="#909090"
                        textTransform="uppercase"
                        letterSpacing="0.0625rem"
                      >
                        {primaryCategory(post)} | Webinar
                      </Box>
                      <Heading
                        as="h1"
                        fontSize={{
                          base: '1.75rem',
                          md: '2rem',
                        }}
                        fontWeight="600"
                        lineHeight={{
                          base: '2.125rem',
                          md: '2.25rem',
                        }}
                        mt="1rem"
                        mb="1rem"
                        color="#2D445D"
                      >
                        {post.title}
                      </Heading>
                      {filterSinglePostByCategory(post, 'Upcoming') && (
                        <Box
                          mb="6"
                          fontSize="1.125rem"
                          lineHeight="1.5rem"
                          fontWeight="700"
                          color="#2D445D"
                        >
                          {date} • {post.webinarsACF.time} PT
                        </Box>
                      )}
                      {post.webinarsACF.description && (
                        <Text
                          fontSize="0.9375rem"
                          lineHeight="1.5rem"
                          dangerouslySetInnerHTML={{
                            __html: post.webinarsACF.description,
                          }}
                        />
                      )}
                    </GridItem>
                  </>
                )}
                <GridItem order={{ lg: '2' }}>
                  {!filterSinglePostByCategory(post, 'Webinar Series') && (
                    <>
                      <Box
                        fontFamily="body"
                        fontSize="0.625rem"
                        lineHeight="0.8125rem"
                        fontWeight="600"
                        color="#909090"
                        textTransform="uppercase"
                        letterSpacing="0.0625rem"
                      >
                        {primaryCategory(post)} | Webinar
                      </Box>
                      <Heading
                        as="h1"
                        fontSize={{
                          base: '1.75rem',
                          md: '2rem',
                        }}
                        fontWeight="600"
                        lineHeight={{
                          base: '2.125rem',
                          md: '2.25rem',
                        }}
                        mt="1rem"
                        mb="1rem"
                        color="#2D445D"
                      >
                        {post.title}
                      </Heading>
                      {filterSinglePostByCategory(post, 'Upcoming') && (
                        <Box
                          mb="6"
                          fontSize="1.125rem"
                          lineHeight="1.5rem"
                          fontWeight="700"
                          color="#2D445D"
                        >
                          {date} • {post.webinarsACF.time} PT
                        </Box>
                      )}
                    </>
                  )}
                  {post.webinarsACF.speakers.map((speaker) => {
                    return (
                      <Flex key={speaker.name} alignItems="center" mb="1rem">
                        <Box
                          w={{ base: '4rem', lg: '5rem' }}
                          h={{ base: '4rem', lg: '5rem' }}
                          position="relative"
                          borderRadius="50%"
                          overflow="hidden"
                          mr="1.5rem"
                          flexShrink="0"
                        >
                          <ImageShimmer
                            src={speaker.image.sourceUrl}
                            alt={speaker.name}
                            style={{ objectFit: 'cover' }}
                            fill
                          />
                        </Box>
                        <Box color="#2D445D">
                          <Box
                            fontSize={{ base: '1rem', lg: '1.125rem' }}
                            lineHeight={{ base: '1.2rem', lg: '1.375rem' }}
                            fontWeight="700"
                            mb="0.125rem"
                          >
                            {speaker.name}
                          </Box>
                          <Box
                            fontSize={{ base: '1rem', lg: '1rem' }}
                            lineHeight={{ base: '1.2rem', lg: '1.375rem' }}
                            fontWeight="600"
                          >
                            {speaker.jobTitle}
                          </Box>
                        </Box>
                      </Flex>
                    );
                  })}
                  <Box
                    w="100%"
                    height={{ base: '0.0625rem' }}
                    m={{ base: '2.4rem 0 2rem', lg: '3rem 0 2.5rem' }}
                    bgColor="#D8D8D8"
                  />
                  <Text
                    fontSize="0.9375rem"
                    lineHeight="1.5rem"
                    dangerouslySetInnerHTML={{
                      __html: post.webinarsACF.text,
                    }}
                  />
                  <Box
                    fontFamily="body"
                    fontSize="1.125rem"
                    mt="14"
                    lineHeight="1.5rem"
                    fontWeight="700"
                  >
                    Share
                  </Box>
                  <Flex
                    fontFamily="var(--chakra-fonts-body)"
                    fontWeight="600"
                    fontSize="1.125rem"
                    lineHeight="1.25rem"
                    mt={{ base: '0.5rem' }}
                  >
                    <Box mr={{ base: '1rem' }}>
                      <FacebookShareButton url={sitePath(router.asPath)} quote={post.title}>
                        Facebook
                      </FacebookShareButton>
                    </Box>
                    <Box mr={{ base: '1rem' }}>
                      <TwitterShareButton url={sitePath(router.asPath)} title={post.title}>
                        Twitter
                      </TwitterShareButton>
                    </Box>
                    <Box mr={{ base: '1rem' }}>
                      <LinkedinShareButton url={sitePath(router.asPath)}>
                        LinkedIn
                      </LinkedinShareButton>
                    </Box>
                    <Box mr={{ base: '1rem' }}>
                      <EmailShareButton
                        url={sitePath(router.asPath)}
                        subject={post.title}
                        body={post.title}
                        openShareDialogOnClick={true}
                        onClick={(e) => e.preventDefault()}
                      >
                        Email
                      </EmailShareButton>
                    </Box>
                  </Flex>
                </GridItem>
                <GridItem order={{ lg: '1' }}>
                  <Box bgColor="#F6F9FA" padding={{ base: '2rem 0', md: '2.5rem 0' }} maxW="35rem">
                    <Box
                      fontWeight="700"
                      fontSize="1.25rem"
                      lineHeight="1.625rem"
                      color="#2D445D"
                      mb="2rem"
                      px={{ base: '4', md: '20', lg: '10' }}
                    >
                      {filterSinglePostByCategory(post, 'Upcoming') ? 'Register' : 'Watch'} now:
                    </Box>
                    {post.webinarsACF.pardotFormUrl && (
                      <iframe
                        src={post.webinarsACF.pardotFormUrl + utmParams}
                        width="100%"
                        height="595"
                        padding="0"
                        type="text/html"
                        frameBorder="0"
                      />
                    )}
                  </Box>
                </GridItem>
              </Grid>
            </>
          ) : (
            <Grid
              templateColumns={{ base: '1fr', lg: '8fr 4fr' }}
              gap={{ base: '1.6rem', lg: '2rem 4.375rem' }}
              mb={{ base: '2.8rem', lg: '4.5rem' }}
            >
              <GridItem>
                <Box
                  fontFamily="body"
                  fontSize="0.625rem"
                  lineHeight="0.8125rem"
                  fontWeight="600"
                  color="#909090"
                  textTransform="uppercase"
                  letterSpacing="0.0625rem"
                >
                  Webinar | {primaryCategory(post)}
                </Box>
                <Heading
                  as="h1"
                  fontSize={{
                    base: '1.8rem',
                    md: '2.25rem',
                  }}
                  fontWeight="600"
                  lineHeight={{
                    base: '2rem',
                    md: '2.5rem',
                  }}
                  mt="1rem"
                  color="#2D445D"
                >
                  {post.title}
                </Heading>
              </GridItem>
              <GridItem display={{ base: 'none', lg: 'block' }} />
              <GridItem order={{ lg: '1' }}>
                {filterSinglePostByCategory(post, 'Upcoming') && (
                  <>
                    <Box
                      fontSize="0.75rem"
                      lineHeight="1.125rem"
                      fontWeight="700"
                      letterSpacing="0.0625rem"
                      textTransform="uppercase"
                      color="#909090"
                      mb="0.5rem"
                    >
                      Event Date
                    </Box>
                    <Box
                      fontSize={{ base: '0.9rem', lg: '1.125rem' }}
                      lineHeight="1.5rem"
                      color="#2D445D"
                      mb={{ base: '1rem', lg: '2.5rem' }}
                    >
                      {date} • {post.webinarsACF.time} PT
                    </Box>
                  </>
                )}
                <Box
                  fontSize="0.75rem"
                  lineHeight="1.125rem"
                  fontWeight="700"
                  letterSpacing="0.0625rem"
                  textTransform="uppercase"
                  color="#909090"
                  mb="1rem"
                >
                  Speakers
                </Box>
                {post.webinarsACF.speakers.map((speaker) => {
                  return (
                    <Flex key={speaker.name} alignItems="center" mb="1rem">
                      <Box
                        w={{ base: '4rem', lg: '5rem' }}
                        h={{ base: '4rem', lg: '5rem' }}
                        position="relative"
                        borderRadius="50%"
                        overflow="hidden"
                        mr="1.5rem"
                        flexShrink="0"
                      >
                        <ImageShimmer
                          src={speaker.image.sourceUrl}
                          alt={speaker.name}
                          style={{ objectFit: 'cover' }}
                          fill
                        />
                      </Box>
                      <Box color="#2D445D">
                        <Box
                          fontSize={{ base: '1rem', lg: '1.125rem' }}
                          lineHeight={{ base: '1.2rem', lg: '1.375rem' }}
                          fontWeight="700"
                          mb="0.125rem"
                        >
                          {speaker.name}
                        </Box>
                        <Box
                          fontSize={{ base: '1rem', lg: '1rem' }}
                          lineHeight={{ base: '1.2rem', lg: '1.375rem' }}
                          fontWeight="600"
                        >
                          {speaker.jobTitle}
                        </Box>
                      </Box>
                    </Flex>
                  );
                })}
              </GridItem>
              <GridItem order={{ lg: '0' }}>
                {post.webinarsACF.youtube && (
                  <AspectRatio maxW="100%" ratio={16 / 9}>
                    <iframe
                      src={`https://www.youtube.com/embed/${post.webinarsACF.youtube}`}
                      frameBorder="0"
                      title="video"
                    />
                  </AspectRatio>
                )}
                <Text
                  fontSize="0.9375rem"
                  lineHeight="1.5rem"
                  mt={{ base: '1.6rem', lg: '2rem' }}
                  dangerouslySetInnerHTML={{
                    __html: post.webinarsACF.text,
                  }}
                />
              </GridItem>
            </Grid>
          )}
        </Container>

        <RelatedPosts
          title="Recommended viewing"
          filteredOutId={post.id}
          posts={relatedPostsWithParams}
        />

        <BlueCTA />
      </Layout>
    </>
  );
}
